import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';
import { hermesAgentApi } from '../services/api';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const STATUSES = ['pending', 'running_stage1', 'running_stage2', 'running_stage3', 'complete', 'failed'];

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  running_stage1: 'bg-blue-100 text-blue-700',
  running_stage2: 'bg-blue-100 text-blue-700',
  running_stage3: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const ViewIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const topicLabel = (topic) => (topic && topic.trim() ? topic : 'general UK small business');

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status || 'pending'}
  </span>
);

const isPrimitive = (v) => v === null || v === undefined || typeof v !== 'object';

// Recursive, schema-agnostic renderer for the stage JSON blobs.
const JsonNode = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">—</span>;
  }
  if (typeof value === 'string') {
    if (!value.trim()) return <span className="text-gray-400 italic">—</span>;
    return <span className="text-gray-800 whitespace-pre-wrap break-words">{value}</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="font-mono text-indigo-600">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400 italic">Empty list</span>;
    return (
      <div className="space-y-2">
        {value.map((item, i) => (
          isPrimitive(item) ? (
            <div key={i} className="flex gap-2">
              <span className="select-none text-gray-300">•</span>
              <div className="flex-1 text-sm"><JsonNode value={item} /></div>
            </div>
          ) : (
            <div key={i} className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <JsonNode value={item} />
            </div>
          )
        ))}
      </div>
    );
  }
  const entries = Object.entries(value);
  if (entries.length === 0) return <span className="text-gray-400 italic">Empty</span>;
  return (
    <div className="space-y-2.5">
      {entries.map(([key, val]) => (
        <div key={key} className={isPrimitive(val) ? 'flex flex-col sm:flex-row sm:gap-3' : ''}>
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 sm:min-w-[150px] sm:pt-0.5">
            {key.replace(/_/g, ' ')}
          </span>
          <div className="flex-1 text-sm">
            {isPrimitive(val) ? (
              <JsonNode value={val} />
            ) : (
              <div className="mt-1 border-l-2 border-gray-100 pl-3"><JsonNode value={val} /></div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const StagePanel = ({ data }) => {
  const [raw, setRaw] = useState(false);
  const hasData = data !== null && data !== undefined && !(typeof data === 'object' && Object.keys(data).length === 0);

  if (!hasData) {
    return <div className="py-10 text-center text-sm text-gray-400">No data for this stage yet.</div>;
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={() => setRaw((r) => !r)}
          className="text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          {raw ? 'Formatted view' : 'Raw JSON'}
        </button>
      </div>
      {raw ? (
        <pre className="overflow-x-auto rounded-md bg-gray-900 p-4 text-xs leading-relaxed text-gray-100">
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <JsonNode value={data} />
      )}
    </div>
  );
};

const DETAIL_TABS = [
  { key: 'summary', label: 'Log' },
  { key: 'stage1_json', label: 'Stage 1' },
  { key: 'stage2_json', label: 'Stage 2' },
  { key: 'stage3_json', label: 'Stage 3' },
  { key: 'analysis_json', label: 'Analysis' },
];

// Field names vary a little across pipeline versions, so read the first key that's present.
const firstValue = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '')) return v;
  }
  return null;
};

// Stage 3 is normally an array of evaluated use cases; tolerate an object wrapper too.
const stageItems = (stage) => {
  if (Array.isArray(stage)) return stage;
  if (stage && typeof stage === 'object') {
    for (const k of ['usecases', 'use_cases', 'results', 'items', 'candidates', 'evaluations']) {
      if (Array.isArray(stage[k])) return stage[k];
    }
    const arr = Object.values(stage).find(Array.isArray);
    if (arr) return arr;
  }
  return [];
};

const PROBLEM_KEYS = ['problem_area', 'problem', 'problem_space', 'opportunity', 'use_case', 'usecase', 'name', 'title', 'idea', 'solution', 'area'];
const REASONING_KEYS = ['reasoning', 'rationale', 'reason', 'justification'];
const CALL_KEYS = ['final_call', 'call', 'decision'];

const isPursue = (item) => {
  const call = firstValue(item, CALL_KEYS);
  return typeof call === 'string' && call.toLowerCase().includes('pursue');
};

// The problem_area is defined in the discover/validate stages, not repeated in the
// stage-3 kill-test verdict — so resolve it by use-case id across every stage.
const buildProblemMap = (run) => {
  const map = {};
  [run.stage1_json, run.stage2_json, run.stage3_json].forEach((stage) => {
    stageItems(stage).forEach((item) => {
      const id = firstValue(item, ['id']);
      const problem = firstValue(item, PROBLEM_KEYS);
      if (id != null && problem && map[String(id)] == null) {
        map[String(id)] = problem;
      }
    });
  });
  return map;
};

// Derived overview shown above the tabs: how many use cases to pursue, and why.
const PursueSummary = ({ run }) => {
  const items = stageItems(run.stage3_json);
  if (!items.length) return null;
  const pursues = items.filter(isPursue);
  const problemMap = buildProblemMap(run);

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-green-700">{pursues.length}</span>
        <span className="text-sm font-medium text-gray-700">
          {pursues.length === 1 ? 'use case to pursue' : 'use cases to pursue'}
          <span className="text-gray-400"> of {items.length} evaluated</span>
        </span>
      </div>

      {pursues.length > 0 && (
        <div className="mt-3 space-y-2">
          {pursues.map((item, i) => {
            const id = firstValue(item, ['id']);
            const problem = firstValue(item, PROBLEM_KEYS) || (id != null ? problemMap[String(id)] : null);
            const reasoning = firstValue(item, REASONING_KEYS);
            return (
              <div key={i} className="rounded-md border border-green-200 bg-white p-3">
                {(id || problem) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {id && <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">{String(id)}</span>}
                    {problem && <span className="text-sm font-semibold text-gray-900">{String(problem)}</span>}
                  </div>
                )}
                {reasoning
                  ? <p className={`whitespace-pre-wrap text-sm text-gray-700 ${(id || problem) ? 'mt-1.5' : ''}`}>{String(reasoning)}</p>
                  : (!id && !problem) && <p className="text-sm italic text-gray-400">Pursue</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RunDetailModal = ({ run, onClose }) => {
  const [tab, setTab] = useState('stage1_json');
  const tabHasData = (key) => {
    const d = run[key];
    if (d === null || d === undefined) return false;
    if (typeof d === 'string') return d.trim() !== '';
    if (typeof d === 'object') return Object.keys(d).length > 0;
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{topicLabel(run.topic)}</h3>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={run.status} />
                {typeof run.search_count === 'number' && (
                  <span className="text-xs text-gray-500">{run.search_count} searches</span>
                )}
              </div>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500 sm:grid-cols-3">
            <div><span className="font-medium text-gray-600">Requested:</span> {formatDate(run.requested_at)}</div>
            <div><span className="font-medium text-gray-600">Started:</span> {formatDate(run.started_at)}</div>
            <div><span className="font-medium text-gray-600">Completed:</span> {formatDate(run.completed_at)}</div>
          </div>

          <PursueSummary run={run} />
          {run.error && (
            <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{run.error}</p>
          )}
        </div>

        <div className="flex gap-1 border-b border-gray-100 px-6 pt-3">
          {DETAIL_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`relative rounded-t-md px-4 py-2 text-sm font-medium transition ${
                tab === key ? 'bg-white text-primary-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
              {tabHasData(key) && (
                <span className={`ml-1.5 inline-block h-1.5 w-1.5 rounded-full ${tab === key ? 'bg-primary-500' : 'bg-gray-300'}`} />
              )}
              {tab === key && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-500" />}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === 'summary' ? (
            run.summary
              ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{run.summary}</p>
              : <div className="py-10 text-center text-sm text-gray-400">No log summary for this run.</div>
          ) : (
            <StagePanel data={run[tab]} />
          )}
        </div>
      </div>
    </div>
  );
};

const RunEditModal = ({ run, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState({
    topic: run?.topic || '',
    status: run?.status || 'pending',
    summary: run?.summary || '',
  });

  useEffect(() => {
    setForm({
      topic: run?.topic || '',
      status: run?.status || 'pending',
      summary: run?.summary || '',
    });
  }, [run]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-lg font-semibold">{run ? 'Edit Run' : 'New Run'}</h3>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-4">
          <label className={labelCls}>Topic</label>
          <input
            type="text"
            value={form.topic}
            onChange={(e) => setField('topic', e.target.value)}
            className={inputCls}
            placeholder="Leave blank for general UK small business"
          />
        </div>
        <div className="mb-4">
          <label className={labelCls}>Status</label>
          <select
            value={form.status}
            onChange={(e) => setField('status', e.target.value)}
            className={inputCls}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="mb-6">
          <label className={labelCls}>Summary</label>
          <textarea
            value={form.summary}
            onChange={(e) => setField('summary', e.target.value)}
            rows={3}
            className={inputCls + ' resize-vertical'}
            placeholder="Short pursue/park/kill summary"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onSave(run?.id, form, !run?.id)}
            disabled={saving}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700 disabled:opacity-60"
          >
            {saving ? 'Saving…' : run ? 'Save changes' : 'Create run'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium transition hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const HermesAgent = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRun, setEditingRun] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteRunId, setDeleteRunId] = useState(null);
  const [viewingRun, setViewingRun] = useState(null);

  const loadRuns = async () => {
    try {
      setLoading(true);
      const res = await hermesAgentApi.getAll();
      setRuns(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const closeModal = () => {
    setIsCreating(false);
    setEditingRun(null);
    setError('');
  };

  const handleSave = async (id, payload, isNew = false) => {
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await hermesAgentApi.create(payload);
      } else {
        await hermesAgentApi.update(id, payload);
      }
      await loadRuns();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await hermesAgentApi.delete(id);
      setDeleteRunId(null);
      await loadRuns();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hermes Agent</h1>
            <p className="mt-1 text-sm text-gray-500">Manage use-case pipeline runs and visualise stage output.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadRuns}
              title="Refresh"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600 shadow-md transition hover:bg-gray-300"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              title="New run"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white shadow-md transition hover:bg-primary-700"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="hidden grid-cols-12 gap-4 border-b border-gray-100 px-6 py-3 text-xs uppercase tracking-wide text-gray-500 md:grid">
            <span className="col-span-3">Topic</span>
            <span className="col-span-2">Status</span>
            <span className="col-span-1">Searches</span>
            <span className="col-span-2">Summary</span>
            <span className="col-span-2">Requested</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
            </div>
          ) : runs.length === 0 ? (
            <div className="px-6 py-14 text-center text-sm text-gray-500">
              No runs yet. Create one, or they will appear here as the pipeline populates them.
            </div>
          ) : (
            runs.map((run) => (
              <div key={run.id} className="grid grid-cols-1 gap-4 border-b border-gray-100 px-6 py-4 last:border-0 md:grid-cols-12 md:items-center">
                <div className="md:col-span-3">
                  <p className="truncate text-sm font-medium text-gray-900">{topicLabel(run.topic)}</p>
                </div>
                <div className="md:col-span-2">
                  <StatusBadge status={run.status} />
                </div>
                <div className="text-sm text-gray-600 md:col-span-1">
                  {typeof run.search_count === 'number' ? run.search_count : '—'}
                </div>
                <div className="md:col-span-2">
                  <p className="line-clamp-2 break-words text-sm text-gray-700">{run.summary || '—'}</p>
                </div>
                <div className="text-sm text-gray-400 md:col-span-2">{formatDate(run.requested_at)}</div>
                <div className="flex items-center gap-1 md:col-span-2 md:justify-end">
                  <button
                    type="button"
                    onClick={() => setViewingRun(run)}
                    title="Visualise stages"
                    className="rounded-md bg-indigo-50 p-2 text-indigo-500 transition hover:bg-indigo-100"
                  >
                    <ViewIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingRun(run)}
                    title="Edit run"
                    className="rounded-md bg-gray-100 p-2 text-gray-500 transition hover:bg-gray-200"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteRunId(run.id)}
                    title="Delete run"
                    className="rounded-md bg-red-50 p-2 text-red-400 transition hover:bg-red-100"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {(isCreating || editingRun) && (
        <RunEditModal
          run={editingRun}
          error={error}
          saving={saving}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}

      {viewingRun && (
        <RunDetailModal run={viewingRun} onClose={() => setViewingRun(null)} />
      )}

      {deleteRunId && (
        <ConfirmModal
          message="Delete this run? This action cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onCancel={() => setDeleteRunId(null)}
          onConfirm={() => handleDelete(deleteRunId)}
        />
      )}
    </div>
  );
};

export default HermesAgent;
